import React, { useState, useEffect, useContext } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Input from '@mui/material/Input';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useOktaAuth } from '@okta/okta-react';
import { usePartnerManagerAPI } from '../../as2-onboarding-component/hooks/useClient';
import { UserDetailsContext } from '../../../context/UserDetailsContext';
import { DraftData, DynamoDBItemWithDraft, TransactionInfo, TransactionInfoData, UnitInfo, UnitInfoData, PartnerManagerEvent } from '../types';
import { TASK_STATES } from '../constants';

const SPACING = {
    BETWEEN_CARDS: '24px',
    WITHIN_CARD: '16px',
    BETWEEN_EQUAL: '8px',
};


const STAGING_ENVIRONMENT = process.env.REACT_APP_PARTNER_MANAGER_STAGING_ENV || 'qual';

/**
 * Props for the ReadOnlyField component in GeneratedInformation
 */
export interface ReadOnlyFieldProps {
    label: string;
    value: any;
}

/**
 * Props for the EditableField component in GeneratedInformation
 */
export interface EditableFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
}

/**
 * Props for the TransactionRow component in GeneratedInformation
 */
export interface TransactionRowProps {
    code: string;
    label: string;
    transaction: TransactionInfoData;
    isEditable: boolean;
    onFieldChange: (field: keyof TransactionInfoData, value: string) => void;
}

/**
 * Props for the GeneratedInformation component
 */
export interface GeneratedInfoProps {
    task: DynamoDBItemWithDraft;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    onCreatePartnerSuccess?: () => void;
}


const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
    <Box sx={{ width: '100%' }}>
        <Typography variant="labelMedium" sx={{ display: 'block', mb: SPACING.BETWEEN_EQUAL }}>
            {label}
        </Typography>
        <Input
            fullWidth
            value={value}
            readOnly
            disableUnderline
            sx={{
                width: '100%',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '6px 12px',
                '&:before': { display: 'none' },
                '&:after': { display: 'none' },
            }}
        />
    </Box>
);

const EditableField: React.FC<EditableFieldProps> = ({ label, value, onChange }) => {
    const isEmpty = !value || value.trim() === '';
    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="labelMedium" sx={{ display: 'block', mb: SPACING.BETWEEN_EQUAL }}>
                {label}
            </Typography>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <Input
                    fullWidth
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                    disableUnderline
                    sx={{
                        width: '100%',
                        border: `1px solid ${isEmpty ? '#d32f2f' : '#e0e0e0'}`,
                        borderRadius: '4px',
                        padding: '6px 12px',
                        paddingRight: isEmpty ? '100px' : '12px',
                        backgroundColor: '#fff',
                        '&:hover': { borderColor: isEmpty ? '#d32f2f' : '#9e9e9e' },
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                    }}
                />
                {isEmpty && (
                    <Box sx={{ position: 'absolute', right: '8px', display: 'flex', alignItems: 'center', gap: '4px', pointerEvents: 'none' }}>
                        <Chip
                            label="Required"
                            size="small"
                            variant="badge"
                            icon={<ErrorOutlineIcon sx={{ fontSize: '11px !important', color: '#b71c1c !important' }} />}
                            sx={{
                                fontSize: '10px',
                                height: '18px',
                                backgroundColor: '#fce8e8',
                                border: '1px solid #ef9a9a',
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const TransactionRow: React.FC<TransactionRowProps> = ({ code, label, transaction, isEditable, onFieldChange }) => {
    const [generate997, setGenerate997] = useState<boolean>(transaction.gen997 === 'Y');

    const handleGen997Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setGenerate997(checked);
        onFieldChange('gen997', checked ? 'Y' : 'N');
    };

    return (
        <Box sx={{ mt: SPACING.BETWEEN_CARDS, pt: SPACING.BETWEEN_CARDS }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.BETWEEN_EQUAL, mb: SPACING.WITHIN_CARD }}>
                <Chip label={code} color="success" size="medium" variant="badge" />
                <Typography variant="h3">{label}</Typography>
                {transaction.function && (
                    <Chip
                        label={transaction.function === 'I' ? 'Inbound' : 'Outbound'}
                        color={transaction.function === 'I' ? 'informational' : 'warning'}
                        size="small"
                        variant="badge"
                    />
                )}
            </Box>
            <Grid container spacing={20} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 3 }}>
                    {isEditable ? (
                        <EditableField
                            label="EDI-DCM Map Name"
                            value={transaction.edidcm_map_name || ''}
                            onChange={(v: string) => onFieldChange('edidcm_map_name', v)}
                        />
                    ) : (
                        <ReadOnlyField label="EDI-DCM Map Name" value={transaction.edidcm_map_name || ''} />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    {isEditable ? (
                        <EditableField
                            label="DCM-EDI Map Name"
                            value={transaction.dcmedi_map_name || ''}
                            onChange={(v: string) => onFieldChange('dcmedi_map_name', v)}
                        />
                    ) : (
                        <ReadOnlyField label="DCM-EDI Map Name" value={transaction.dcmedi_map_name || ''} />
                    )}
                </Grid>
               <Grid size={{ xs: 12, sm: 3 }} >
                    {isEditable ? (
                        <EditableField 
                        
                            label="SAP Landscape"
                            value={transaction.sap_landscape || ''}
                            onChange={(v: string) => onFieldChange('sap_landscape', v)}
                        />
                    ) : (
                        <ReadOnlyField label="SAP Landscape" value={transaction.sap_landscape || ''} />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }} >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={generate997}
                                onChange={handleGen997Change}
                                disabled={!isEditable}
                                
                            />
                        }
                        label="Generate 997"
                        labelPlacement="top"
                        sx={{ mt: 0 }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

const getTabLabel = (unit: UnitInfo, isActiveTab: boolean, hasConfigError: boolean) => {
    const showAlert = isActiveTab ? hasConfigError : !unit.completion;
    const unitName = unit.data.unit_name;
    const labelText = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
            <span>{unit.data.unit_code}</span>
            {unitName && (
                <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', opacity: 0.7, fontSize: '0.7rem' }}>
                    {unitName}
                </span>
            )}
        </Box>
    );

    if (showAlert) {
        return (
            <Tooltip title={unitName || unit.data.unit_code}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {labelText}
                    <ErrorOutlineIcon sx={{ fontSize: '16px', color: '#d32f2f' }} />
                </Box>
            </Tooltip>
        );
    }
    return unitName ? <Tooltip title={unitName}>{labelText}</Tooltip> : unit.data.unit_code;
};

const GeneratedInformation: React.FC<GeneratedInfoProps> = ({
    task,
    expanded,
    onExpandedChange,
    onCreatePartnerSuccess,
}) => {

    const [selectedUnitTab, setSelectedUnitTab] = useState<number>(0);
    const [draftEdits, setDraftEdits] = useState<DraftData | null>(null); //To identify if user has made any edits to the draft data
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const { oktaAuth } = useOktaAuth();
    const partnerManagerClient = usePartnerManagerAPI(oktaAuth.getAccessToken());
    const { oktaDetails } = useContext(UserDetailsContext);

    const isEditable = true;    //TODO Rollback isEditable if (task.state === 'DRAFT_FAILED') - Only allow edits if the task is in DRAFT_FAILED state. 
    // For now, we are allowing edits for all states.

    const displayData = draftEdits || task.draft_data;

    // Clear local edits when task updates to DRAFT_COMPLETED (via AppSync)
    useEffect(() => {
        if (task.state === TASK_STATES.DRAFT_COMPLETED && draftEdits) {
            setDraftEdits(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task.state]);


    if (!task) return null;

    // Show generating state when task is NEW
    if (task.state === TASK_STATES.NEW) {
        return (
            <Accordion
                defaultExpanded
                expanded={expanded}
                onChange={(_event, isExpanded) => onExpandedChange?.(isExpanded)}
                sx={{
                    borderRadius: '8px !important',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1) !important',
                    border: 'none',
                    '&:before': { display: 'none' },
                    '&.MuiAccordion-root': { border: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.WITHIN_CARD }}>
                        <Typography variant="h2">Generate Information</Typography>
                        <Chip
                            label="Generating..."
                            color="warning"
                            variant="badge"
                            size="small"
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: SPACING.BETWEEN_CARDS, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, mb: 5 }}>
                        <Typography variant="bodyMedium" color="text.secondary">
                            Generating draft data from request...
                        </Typography>
                        <CircularProgress />
                    </Box>
                </AccordionDetails>
            </Accordion>
        );
    }



    const { supplier_info, unit_info } = displayData;

    const handleUnitFieldChange = (unitIndex: number, field: keyof UnitInfoData, value: string) => {
        setDraftEdits(prev => {
            const draft = prev ? structuredClone(prev) : structuredClone(task.draft_data);
            draft.unit_info[unitIndex].data[field] = value;
            return draft;
        });
    };

    const handleTransactionFieldChange = (unitIndex: number, txIndex: number, field: keyof TransactionInfoData, value: string) => {
        setDraftEdits(prev => {
            const draft = prev ? structuredClone(prev) : structuredClone(task.draft_data);
            (draft.unit_info[unitIndex].transaction_info[txIndex].data as any)[field] = value;
            return draft;
        });
    };

    const handleCreatePartner = async () => {
        if (!draftEdits) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            const event: PartnerManagerEvent  = {
                trace_id: crypto.randomUUID(),
                assigned_to: `${oktaDetails.userid} (${oktaDetails.username})`,
                correlation_id: task.id,
                request_id: task.request_id,
                current_phase: task.current_phase,
                source_id: 'partner-manager.frontend',
                source_type: 'Human',
                state: TASK_STATES.DRAFT_APPROVED,
                detail_type: 'DraftApproved',
                detail: draftEdits,
                version: '1.0',
                target_env: STAGING_ENVIRONMENT,
                source_env: task.source_env,
            };

            await partnerManagerClient.post(`/actions/${task.id}`, event);            
            onCreatePartnerSuccess?.();
        } catch (err: any) {
            setSaveError(err?.message || 'Failed to apply changes');
        } finally {
            setIsSaving(false);
        }
    };

    const handleUnitTabChange = (_event: any, newValue: number) => {
        setSelectedUnitTab(newValue);
    };

    const configFields: (keyof UnitInfoData)[] = ['isa_id', 'gs_id', 'isa_qualifier', 'element_separator', 'subelement_separator', 'segment_terminator'];
    const currentUnitData = unit_info[selectedUnitTab]?.data;
    const hasConfigError = currentUnitData && configFields.some((f) => !currentUnitData[f] || String(currentUnitData[f]).trim() === '');

    const getStatusChip = () => {
        if (isSaving) {
            return (
                <Chip
                    label="Saving..."
                    color="warning"
                    variant="badge"
                    size="small"
                />
            );
        }
        if (task.state === TASK_STATES.DRAFT_COMPLETED || task.state === TASK_STATES.DRAFT_APPROVED) {
            return (
                <Chip
                    label="Completed"
                    color="success"
                    icon={<fuel-icon name="check_circle" />}
                    variant="badge"
                    size="small"
                />
            );
        }
        return (
            <Chip
                label="Action Required"
                color="error"
                icon={<ErrorOutlineIcon sx={{ fontSize: '14px !important' }} />}
                variant="badge"
                size="small"
            />
        );
    };

    return (
        <Accordion
            defaultExpanded
            expanded={expanded}
            onChange={(_event, isExpanded) => onExpandedChange?.(isExpanded)}
            sx={{
                borderRadius: '8px !important',
                boxShadow: '0 1px 4px rgba(0,0,0,0.1) !important',
                border: 'none',
                '&:before': { display: 'none' },
                '&.MuiAccordion-root': { border: 'none' },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.WITHIN_CARD }}>
                    <Typography variant="h2">Generate Information</Typography>
                    {getStatusChip()}
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: SPACING.BETWEEN_CARDS, display: 'flex', flexDirection: 'column', gap: SPACING.BETWEEN_CARDS }}>
                {/* Supplier Information */}
                <Box sx={{ p: SPACING.WITHIN_CARD, borderRadius: '8px', backgroundColor: '#fafafa' }}>
                    <Typography variant="h3" sx={{ mb: SPACING.WITHIN_CARD }}>
                        Supplier Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ReadOnlyField label="Supplier Number" value={supplier_info.data.partner_number} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ReadOnlyField label="Partner ID" value={supplier_info.data.sap_partner_id} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ReadOnlyField label="Supplier Name" value={supplier_info.data.partner_name} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: SPACING.WITHIN_CARD }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ReadOnlyField label="Qualifier" value={supplier_info.data.isa_id_qual} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ReadOnlyField label="Interchange ID" value={supplier_info.data.isa_mailbox_id} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <ReadOnlyField label="Group ID" value={supplier_info.data.gs_mailbox_id} />
                        </Grid>
                    </Grid>
                </Box>

                {/* EDI Configuration by Unit & Transaction */}
                <Box sx={{ p: SPACING.WITHIN_CARD, borderRadius: '8px', backgroundColor: '#fafafa' }}>
                    <Typography variant="h3" sx={{ mb: SPACING.WITHIN_CARD }}>
                        EDI Configuration by Unit & Transaction
                    </Typography>

                    <Tabs onChange={handleUnitTabChange} value={selectedUnitTab}>
                        {unit_info.map((unit: UnitInfo, index) => (
                            <Tab
                                key={unit.data.unit_code}
                                id={`unit-tab-${index}`}
                                label={getTabLabel(unit, index === selectedUnitTab, hasConfigError)}
                            />
                        ))}
                    </Tabs>

                    {unit_info.map((unit: UnitInfo, index) => {
                        const unitData: UnitInfoData = unit.data;
                        const transactions: TransactionInfo[] = unit.transaction_info;

                        return (
                            <Card
                                hidden={selectedUnitTab !== index}
                                id={`unit-tabpanel-${index}`}
                                key={unitData.unit_code}
                                role="tabpanel"
                                sx={{ boxShadow: 'none', border: 'none', backgroundColor: 'transparent' }}
                            >
                                {selectedUnitTab === index && (
                                    <CardContent sx={{ px: 0, pt: SPACING.WITHIN_CARD }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                {isEditable ? (
                                                    <EditableField
                                                        label="Interchange Sender ID"
                                                        value={unitData.isa_id}
                                                        onChange={(v) => handleUnitFieldChange(index, 'isa_id', v)}
                                                    />
                                                ) : (
                                                    <ReadOnlyField label="Interchange Sender ID" value={unitData.isa_id} />
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                {isEditable ? (
                                                    <EditableField
                                                        label="Group Sender ID"
                                                        value={unitData.gs_id}
                                                        onChange={(v) => handleUnitFieldChange(index, 'gs_id', v)}
                                                    />
                                                ) : (
                                                    <ReadOnlyField label="Group Sender ID" value={unitData.gs_id} />
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                {isEditable ? (
                                                    <EditableField
                                                        label="Qualifier"
                                                        value={unitData.isa_qualifier}
                                                        onChange={(v) => handleUnitFieldChange(index, 'isa_qualifier', v)}
                                                    />
                                                ) : (
                                                    <ReadOnlyField label="Qualifier" value={unitData.isa_qualifier} />
                                                )}
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={2} sx={{ mt: SPACING.WITHIN_CARD }}>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                {isEditable ? (
                                                    <EditableField
                                                        label="Element Separator"
                                                        value={unitData.element_separator}
                                                        onChange={(v) => handleUnitFieldChange(index, 'element_separator', v)}
                                                    />
                                                ) : (
                                                    <ReadOnlyField label="Element Separator" value={unitData.element_separator} />
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                {isEditable ? (
                                                    <EditableField
                                                        label="Sub element Separator"
                                                        value={unitData.subelement_separator}
                                                        onChange={(v) => handleUnitFieldChange(index, 'subelement_separator', v)}
                                                    />
                                                ) : (
                                                    <ReadOnlyField label="Sub element Separator" value={unitData.subelement_separator} />
                                                )}
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 4 }}>
                                                {isEditable ? (
                                                    <EditableField
                                                        label="Segment Terminator"
                                                        value={unitData.segment_terminator}
                                                        onChange={(v) => handleUnitFieldChange(index, 'segment_terminator', v)}
                                                    />
                                                ) : (
                                                    <ReadOnlyField label="Segment Terminator" value={unitData.segment_terminator} />
                                                )}
                                            </Grid>
                                        </Grid>

                                        <Divider sx={{ my: SPACING.WITHIN_CARD }} />

                                        {transactions.map((tx: TransactionInfo, txIdx) => {
                                            const transactionData = tx.data;

                                            return (
                                                <React.Fragment key={transactionData.transcode}>
                                                    <TransactionRow
                                                        code={transactionData.transcode}
                                                        label={transactionData.transcode}
                                                        transaction={transactionData}
                                                        isEditable={isEditable}
                                                        onFieldChange={(field, value) => handleTransactionFieldChange(index, txIdx, field, value)}
                                                    />
                                                    {txIdx < transactions.length - 1 && (
                                                        <Divider sx={{ my: SPACING.WITHIN_CARD }} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </CardContent>
                                )}
                            </Card>
                        );
                    })}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: SPACING.BETWEEN_EQUAL }}>
                    {saveError && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {saveError}
                        </Alert>
                    )}
                    <Typography variant="bodySmall" sx={{ color: '#d32f2f' }}>
                       * Clicking 'Create Partner' will create all new envelopes in the staging environment.
                    </Typography>
                    <Button
                        variant="primary"
                        disabled={!isEditable || !draftEdits || isSaving}
                        onClick={handleCreatePartner}
                        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {isSaving ? 'Creating Partner...' : 'Create Partner'}
                    </Button>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default GeneratedInformation;
